import { rating } from "openskill";

import type { Bet, Player } from "@/types";
import type { ActivityId, BetId, PlayerId } from "@/types/branded";

/**
 * Reset a player's scores, stats, and rating to fresh defaults.
 * Optionally resets betting state as well.
 * Safe to call on Immer drafts.
 */
export const resetPlayerStats = (
  p: Player,
  opts?: { resetBetting?: boolean; bankroll?: number },
): void => {
  p.totalPoints = 0;
  p.stats.gamesPlayed = 0;
  p.stats.winStreak = 0;
  p.rating = rating();
  p.personalBests = { highestPoints: 0, longestStreak: 0, bestRating: 0 };
  if (opts?.resetBetting) {
    p.bettingBankroll = opts.bankroll ?? 100;
    p.bettingStats = { totalBets: 0, correctBets: 0, netEarnings: 0 };
  }
};

/**
 * Mutates a player's `personalBests` to reflect their current stats.
 * Safe to call on Immer drafts.
 */
export const updatePersonalBests = (p: Player): void => {
  const skill = p.rating.mu - 2 * p.rating.sigma;
  if (p.totalPoints > p.personalBests.highestPoints) p.personalBests.highestPoints = p.totalPoints;
  if (p.stats.winStreak > p.personalBests.longestStreak)
    p.personalBests.longestStreak = p.stats.winStreak;
  if (skill > p.personalBests.bestRating) p.personalBests.bestRating = skill;
};

/**
 * Resolve all unresolved bets for an activity, mutating bets and players in place.
 * Safe to call on Immer drafts.
 */
export const resolveBetsInPlace = (
  bets: Record<BetId, Bet>,
  players: Record<PlayerId, Player>,
  activityId: ActivityId,
  winnerPlayerIds: PlayerId[],
): void => {
  for (const bet of Object.values(bets)) {
    if (bet.activityId !== activityId || bet.resolved) continue;
    bet.resolved = true;
    bet.correct = winnerPlayerIds.includes(bet.predictedWinnerId);
    const bettor = players[bet.bettorId];
    if (!bettor) continue;
    if (!bettor.bettingStats)
      bettor.bettingStats = { totalBets: 0, correctBets: 0, netEarnings: 0 };
    bettor.bettingStats.totalBets++;
    if (bet.correct) {
      const payout = Math.round(bet.wager * bet.odds);
      bet.payout = payout;
      bettor.bettingBankroll += payout;
      bettor.bettingStats.correctBets++;
      bettor.bettingStats.netEarnings += payout;
    } else {
      bet.payout = -bet.wager;
      bettor.bettingStats.netEarnings -= bet.wager;
    }
  }
};
